import { ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { UnsubscribedBadge } from '@/common/unsubscribed-badge';
import { SubscriptionsListFilters } from './subscriptions-list-filters';
import {
    subscribersControllerList, SubscribersListResponseDtoRowsItem,
} from '@/api-client/api';
import { PAGE_SIZES } from '@/shared/constants';
import { DataTable, type DataTableColumn } from 'mantine-datatable';
import { Badge, Menu, UnstyledButton } from '@mantine/core';
import {
    IconDotsVertical,
    IconMessage,
    IconUser,
} from '@tabler/icons-react';
import { isUserAdmin } from '@/utils/auth-helpers';
import { formatDateTime } from '@admin-panel/shared';
import { useUserState } from '@/state/user.state';


export function SubscribersList(): ReactNode {
    const user = useUserState();
    const [ searchParams, setSearchParams ] = useSearchParams();
    
    const search = searchParams.get('search') || '';
    const isAdmin = isUserAdmin(user.role);
    
    // const { show: showSendMessage, props: propsSendMessage } = useModal<{ userId: number }>();
    
    const { data, error, isLoading, refetch } = useQuery({
        queryKey: [
            'subscriptions',
            search,
            searchParams.get('sort'),
            searchParams.get('direction'),
            searchParams.get('page'),
            searchParams.get('limit'),
        ],
        queryFn: () =>
            subscribersControllerList({
                page: Number(searchParams.get('page') || 1),
                limit: Number(searchParams.get('limit') || 50),
                sort: searchParams.get('sort') || 'createdAt',
                direction: searchParams.get('direction') as 'asc' | 'desc' || 'desc',
                search,
            }),
        placeholderData: keepPreviousData,
        retry: 1,
    });
    
    const columns: DataTableColumn<SubscribersListResponseDtoRowsItem>[] = [
        {
            title: '',
            width: 50,
            accessor: 'actions',
            render() {
                return (
                    <Menu shadow='md' position='bottom-start'>
                        <Menu.Target>
                            <UnstyledButton>
                                <IconDotsVertical size={ 16 } stroke={ 3 }/>
                            </UnstyledButton>
                        </Menu.Target>
                        
                        <Menu.Dropdown>
                            { isAdmin && (
                                <>
                                    <Menu.Item
                                        leftSection={ <IconMessage size={ 14 }/> }
                                        // onClick={ () =>  showSendMessage({ userId: +row.tgUserId! }) }
                                        disabled
                                    >
                                        Отправить сообщение
                                    </Menu.Item>
                                </>
                            ) }
                        </Menu.Dropdown>
                    </Menu>
                )
            },
        },
        {
            title: 'id',
            accessor: 'id',
        },
        {
            title: 'tgUserId',
            accessor: 'tgUserId',
            render(row) {
                return (
                    <Link to={ `/users/?search=${ row.tgUserId }` }>{ row.tgUserId }</Link>
                );
            },
        },
        {
            title: 'tgUserName',
            accessor: 'tgUserName',
        },
        {
            title: 'tgUserPhone',
            accessor: 'tgUserPhone',
        },
        {
            title: 'tgUserFirstName',
            accessor: 'tgUserFirstName',
        },
        {
            title: 'tgUserLastName',
            accessor: 'tgUserLastName',
        },
        {
            title: 'Дата создания',
            accessor: 'createdAt',
            render(row) {
                return formatDateTime(row.createdAt, true);
            },
        },
        {
            title: 'Зарегистрировался',
            accessor: 'registered',
            render(value) {
                return value ? (
                    <Badge color='green' variant='filled'>
                        <IconUser size={14}/>
                    </Badge>
                ) : null;
            },
        },
        {
            title: 'Ref',
            accessor: 'ref',
        },
        {
            title: 'Рассылка "Не зарег..."',
            accessor: 'notRegisteredMailings',
        },
        {
            title: 'Отписался',
            accessor: 'unsubscribed',
            render(value) {
                return value ? (<UnsubscribedBadge/>) : null;
            },
        },
    ];
    
    if (error) return <span>Error: ${ error.message }</span>;
    

    const limit = parseInt(searchParams.get('limit') || '50');
    
    return (
        <>
            <SubscriptionsListFilters onClickRefresh={ () => refetch() }/>
            <DataTable
                idAccessor='tgUserId'
                columns={ columns }
                records={ (data?.rows || []) }
                totalRecords={ data?.total || 0 }
                page={ parseInt(searchParams.get('page') || '1') }
                recordsPerPage={ limit }
                recordsPerPageOptions={ PAGE_SIZES }
                onPageChange={ (nextPage) => {
                    setSearchParams((prev) => {
                        prev.set('page', String(nextPage));
                        return prev;
                    });
                } }
                onRecordsPerPageChange={ (limit) => {
                    setSearchParams((prev) => {
                        prev.set('limit', String(limit));
                        return prev;
                    });
                } }
                sortStatus={ {
                    columnAccessor: searchParams.get('sort') || 'createdAt',
                    direction: (searchParams.get('direction') as 'asc' | 'desc') || 'desc',
                } }
                onSortStatusChange={ ({ columnAccessor, direction }) => {
                    setSearchParams((prev) => {
                        prev.set('sort', columnAccessor);
                        prev.set('direction', direction);
                        return prev;
                    });
                } }
                fetching={ isLoading }
                loaderSize='sm'
                minHeight={ 150 }
                loaderBackgroundBlur={ 4 }
                pinFirstColumn
                highlightOnHover
            />
            {/*<UserSendMessageModal { ...propsSendMessage } onSubmit={ refetch }/>*/}
        </>
    );
}